import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/base/SEO';
import { CONTACT_FORM_URL } from '@/utils/formConfig';

const CANONICAL_URL = 'https://www.hanmanzhijia.com';

export default function AboutPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫之家',
    description: '韩漫之家是国内领先的韩国漫画在线阅读平台，提供免费下拉式韩漫阅读体验。了解我们的品牌故事、口碑评价和联系方式。',
    url: `${CANONICAL_URL}/about`,
    mainEntity: {
      '@type': 'Organization',
      name: '韩漫之家',
      url: CANONICAL_URL,
      description: '韩漫之家让您免费在线观看下拉式韩漫，精选好看的漫画大全',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@hanmanzhijia.com',
        availableLanguage: ['Chinese'],
      },
    },
  };

  return (
    <>
      <SEO
        title="关于我们"
        description="韩漫之家是国内领先的韩国漫画在线阅读平台。了解我们的品牌故事、读者口碑和联系方式。致力于为广大韩漫爱好者提供优质的免费阅读体验。"
        keywords="韩漫之家,韩国漫画,关于我们,漫画平台介绍"
        canonicalUrl={`${CANONICAL_URL}/about`}
        schema={schema}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Page Hero */}
          <section className="text-center py-10 md:py-14">
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-900 mb-4">
              关于<span className="text-primary-500">韩漫之家</span>
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              这里是数百万韩漫爱好者的聚集地，一个让每个人都能免费享受韩国漫画魅力的平台
            </p>
          </section>

          {/* Brand Story */}
          <section className="mb-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                  我们的品牌故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    韩漫之家成立于2023年，由一群热爱韩国漫画的年轻人共同创立。
                    我们深知韩漫爱好者寻找高质量、免费阅读资源的困难，
                    因此立志打造一个专注韩漫的优质阅读平台。
                  </p>
                  <p>
                    从最初的几十部漫画起步，到现在拥有超过<strong>500部精选韩漫作品</strong>，
                    涵盖浪漫爱情、热血动作、奇幻冒险、悬疑惊悚、轻松喜剧、都市剧情、
                    古风历史和恐怖灵异八大分类，韩漫之家已成为国内领先的韩漫阅读平台。
                  </p>
                  <p>
                    我们坚持<strong>完全免费</strong>的阅读模式，
                    采用先进的下拉式阅读技术，为读者提供流畅、沉浸式的阅读体验。
                    无论您是在通勤路上还是休闲时光，只需一部手机或电脑，
                    就能随时随地畅享精彩的韩漫世界。
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src="https://readdy.ai/api/search-image?query=Cozy%20modern%20reading%20space%20with%20digital%20tablet%20displaying%20webtoon%20comic%2C%20warm%20ambient%20lighting%2C%20bookshelf%20background%2C%20minimalist%20interior%20design%2C%20comfortable%20atmosphere%2C%20warm%20coral%20and%20cream%20color%20palette%2C%20editorial%20photography&width=800&height=600&seq=about-brand-story&orientation=landscape"
                    alt="韩漫之家 - 温馨的数字漫画阅读空间"
                    title="韩漫之家品牌故事"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Mission & Values */}
          <section className="mb-14 py-10 md:py-14 bg-background-100 rounded-lg px-6 md:px-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-8 text-center">
              我们的理念
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-heart-line',
                  title: '热爱韩漫',
                  desc: '我们是最纯粹的韩漫爱好者，用最真诚的态度为读者筛选每一部优质作品',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '品质优先',
                  desc: '严格把控漫画内容质量，从画风到剧情，只推荐真正好看的韩漫作品',
                },
                {
                  icon: 'ri-user-smile-line',
                  title: '读者至上',
                  desc: '不断优化阅读体验，倾听每一位读者的声音，持续改进平台功能',
                },
              ].map((item) => (
                <div key={item.title} className="text-center p-6 rounded-lg bg-background-50">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-14">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-8 text-center">
              读者口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '小雨',
                  rating: 5,
                  text: '韩漫之家是我每天必刷的网站，画质清晰，更新快，而且完全免费！最喜欢《复仇的剑术大师》，画风太赞了！',
                },
                {
                  name: '星辰大海',
                  rating: 5,
                  text: '终于找到一个专注韩漫的平台了，分类很清晰，下拉式阅读体验超级好。推荐给了身边所有喜欢看漫画的朋友！',
                },
                {
                  name: '漫画控小陈',
                  rating: 5,
                  text: '从浪漫到悬疑，韩漫之家的漫画库真的很全。而且网站设计得也很舒服，阅读时不会有乱七八糟的弹窗广告。',
                },
              ].map((review) => (
                <div key={review.name} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }, (_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <p className="text-xs text-foreground-400 font-medium">— {review.name}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Stats */}
          <section className="mb-14">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { value: '500+', label: '精选漫画' },
                { value: '50,000+', label: '更新章节' },
                { value: '100万+', label: '忠实读者' },
                { value: '4.8', label: '平均评分' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-lg bg-background-100">
                  <div className="text-3xl font-bold text-primary-500 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="mb-14">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-800 text-sm mb-1">电子邮箱</h3>
                    <a href="mailto:contact@hanmanzhijia.com" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer" rel="nofollow">
                      contact@hanmanzhijia.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-800 text-sm mb-1">微信公众号</h3>
                    <span className="text-sm text-foreground-500">韩漫之家官方</span>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-weibo-line"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-800 text-sm mb-1">新浪微博</h3>
                    <span className="text-sm text-foreground-500">@韩漫之家</span>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="rounded-lg bg-background-100 p-6 border border-background-200/70">
                <h3 className="font-heading text-lg text-foreground-900 mb-4">发送消息</h3>
                <form
                  data-readdy-form
                  id="about-contact-form"
                  className="space-y-4"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const formData = new FormData(form);

                    const hp = formData.get('website_alt');
                    if (hp && String(hp).trim() !== '') {
                      form.reset();
                      return;
                    }
                    formData.delete('website_alt');

                    try {
                      const res = await fetch(CONTACT_FORM_URL, {
                        method: 'POST',
                        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      });
                      const text = await res.text();
                      let parsed: Record<string, unknown> | null = null;
                      try { parsed = JSON.parse(text); } catch { /* ignore */ }
                      if (res.ok && parsed && (parsed as { code?: string }).code === 'OK') {
                        form.reset();
                        alert('消息已发送成功！我们会尽快回复您。');
                      } else {
                        const msg = (parsed as { meta?: { message?: string }; message?: string } | null)?.meta?.message
                          || (parsed as { message?: string } | null)?.message
                          || '发送失败，请稍后重试';
                        alert(msg);
                      }
                    } catch {
                      alert('网络错误，请稍后重试');
                    }
                  }}
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1">姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1">消息内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的消息（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    className="hp-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    发送消息
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}